import { detectIOS } from '@vkontakte/vkjs';
import { Browser, DeviceInfo, MobileOS } from './types';

function getAndroidVersion(userAgent: string) {
  const match = userAgent.toLowerCase().match(/android\s([0-9\.]*)/i);
  return match ? match[1] : undefined;
}

const detectBrowser = (ua: string): Browser => {
  const isPwaMode = Boolean(
    window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone,
  );

  // Chrome on iOS
  const criosMatch = ua.match(/CriOS\/(\d+(?:\.\d+)*)/);
  if (criosMatch) {
    return { name: 'chrome', version: criosMatch[1], isPwaMode };
  }

  // Samsung Internet (before Chrome check — Samsung UA also contains Chrome/)
  const samsungMatch = ua.match(/SamsungBrowser\/(\d+\.?\d*)/);
  if (samsungMatch) {
    return { name: 'samsung', version: samsungMatch[1], isPwaMode };
  }

  // Chrome on Android / Desktop (exclude Edge)
  const chromeMatch = ua.match(/Chrome\/(\d+\.?\d*)/);
  if (chromeMatch && !/Edg\//i.test(ua)) {
    return { name: 'chrome', version: chromeMatch[1], isPwaMode };
  }

  // Safari (exclude other iOS browsers)
  const safariMatch = ua.match(/Version\/(\d+\.?\d*)/);
  if (safariMatch && /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua)) {
    return { name: 'safari', version: safariMatch[1], isPwaMode };
  }

  return { name: 'other', version: '0.0', isPwaMode };
};

const isTelegram = (): boolean =>
  typeof (window as Window & { TelegramWebviewProxy?: unknown }).TelegramWebviewProxy !== 'undefined';

// UA-маркеры in-app браузеров соцсетей и мессенджеров
const SOCIAL_IN_APP_MARKERS = [
  'FBAN',
  'FBAV',
  'Instagram',
  'VKWebView',
  'VKAndroid',
  'OKApp',
  'WhatsApp',
  'TwitteriPhone',
  'TwitterAndroid',
  'musical_ly',
  'BytedanceWebview',
  'Snapchat',
  'MicroMessenger',
  'Line/',
];

// UA-префиксы нативных VK/OK-приложений (webview открывается внутри самого клиента)
const NATIVE_APP_UA_PREFIXES = [
  'com\\.vk\\.vkclient', // VK, iOS
  'com\\.vk\\.love', // vkdating (standalone), iOS
  'com\\.vkontakte\\.android', // VK, Android
  'com\\.vk\\.dating', // vkdating (standalone), Android
  'ru\\.ok\\.dating', // okdating (standalone), iOS/Android
  'ru\\.ok\\.android', // OK, Android
  'ru\\.odnoklassniki\\.iphone', // OK, iOS
];

// объединяем оба списка в один regexp, чтобы не гонять несколько .test() на каждый UA
const IN_APP_UA_REGEXP = new RegExp([...SOCIAL_IN_APP_MARKERS, ...NATIVE_APP_UA_PREFIXES].join('|'), 'i');

const detectIsInApp = (ua: string): boolean => isTelegram() || IN_APP_UA_REGEXP.test(ua);

function detectMobileOS(ua: string): MobileOS | null {
  const androidVersion = ua && getAndroidVersion(ua);

  if (androidVersion) {
    return { name: 'android', version: androidVersion };
  }

  const { isIOS, iosMajor, iosMinor } = detectIOS();

  if (isIOS) {
    return { name: 'ios', version: `${iosMajor}.${iosMinor}` };
  }

  return null;
}

export function detectDeviceInfo(ua: string): DeviceInfo {
  return {
    mobileOS: detectMobileOS(ua),
    browser: detectBrowser(ua),
    isInApp: detectIsInApp(ua),
    ua: '',
  };
}
